import type { AttachableDocument, Attachment } from "@dune/types";
import {
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Loader,
} from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { ChevronDown, FileText, Paperclip, X } from "lucide-react";
import React, { useRef, useState } from "react";

interface AttachmentHandlerProps {
	attachments: Array<Attachment & { isUploading?: boolean }>;
	onAttachmentAdd: (files: File[]) => void;
	onAttachmentRemove: (id: string) => void;
	existingDocuments?: AttachableDocument[];
	isLoadingDocuments?: boolean;
	onExistingDocumentsAdd?: (documents: AttachableDocument[]) => Promise<void>;
}

export const AttachmentHandler: React.FC<AttachmentHandlerProps> = ({
	attachments,
	onAttachmentAdd,
	onAttachmentRemove,
	existingDocuments = [],
	onExistingDocumentsAdd,
	isLoadingDocuments = false,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isExistingDocumentsDialogOpen, setIsExistingDocumentsDialogOpen] =
		useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const [selectedDocuments, setSelectedDocuments] = useState<
		AttachableDocument[]
	>([]);
	const [isAddingDocuments, setIsAddingDocuments] = useState(false);

	const handleUploadFromComputer = () => {
		setDropdownOpen(false);
		setTimeout(() => {
			fileInputRef.current?.click();
		}, 50);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			onAttachmentAdd(Array.from(e.target.files));
			e.target.value = "";
		}
	};

	const handleExistingDocumentsClick = () => {
		setDropdownOpen(false);
		setTimeout(() => {
			setSelectedDocuments([]);
			setIsExistingDocumentsDialogOpen(true);
		}, 50);
	};

	const handleDocumentToggle = (
		document: AttachableDocument,
		checked: boolean,
	) => {
		if (checked) {
			setSelectedDocuments((prev) => [...prev, document]);
		} else {
			setSelectedDocuments((prev) =>
				prev.filter((doc) => doc.id !== document.id),
			);
		}
	};

	const handleAttachSelectedDocuments = async () => {
		if (selectedDocuments.length > 0 && onExistingDocumentsAdd) {
			setIsAddingDocuments(true);
			try {
				await onExistingDocumentsAdd(selectedDocuments);
				setSelectedDocuments([]);
				setIsExistingDocumentsDialogOpen(false);
			} catch (error) {
				console.error("Erreur lors de l'ajout des documents:", error);
			} finally {
				setIsAddingDocuments(false);
			}
		}
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const showExistingDocumentsOption =
		onExistingDocumentsAdd && existingDocuments.length > 0;

	return (
		<div className="mt-4">
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
				className="hidden"
				multiple
			/>

			{showExistingDocumentsOption ? (
				<DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" className="mb-3">
							<Paperclip className="w-4 h-4 mr-2" />
							Attacher un fichier
							<ChevronDown className="w-4 h-4 ml-2" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent>
						<DropdownMenuItem onClick={handleUploadFromComputer}>
							Télécharger depuis l'ordinateur
						</DropdownMenuItem>
						<DropdownMenuItem onClick={handleExistingDocumentsClick}>
							Attacher un document existant
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			) : (
				<Button
					variant="outline"
					onClick={handleUploadFromComputer}
					className="mb-3"
				>
					<Paperclip className="w-4 h-4 mr-2" />
					Attacher un fichier
				</Button>
			)}

			<Dialog
				open={isExistingDocumentsDialogOpen}
				onOpenChange={setIsExistingDocumentsDialogOpen}
			>
				<DialogContent className="max-w-3xl max-h-[80vh]">
					<DialogHeader>
						<DialogTitle>Attacher un document existant</DialogTitle>
					</DialogHeader>
					<div className="py-4">
						<p className="text-gray-600 mb-4">
							Sélectionnez un ou plusieurs documents existants à attacher à
							votre email.
						</p>

						{isLoadingDocuments ? (
							<div className="flex items-center justify-center py-8">
								<Loader className="mr-2" />
								<span>Chargement des documents...</span>
							</div>
						) : existingDocuments.length === 0 ? (
							<p className="text-sm text-gray-500 py-8 text-center">
								Aucun document disponible
							</p>
						) : (
							<>
								<div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
									{existingDocuments.map((document) => {
										const isSelected = selectedDocuments.some(
											(doc) => doc.id === document.id,
										);

										const handleToggle = () =>
											handleDocumentToggle(document, !isSelected);

										const handleKeyDown = (e: React.KeyboardEvent) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												handleToggle();
											}
										};

										return (
											<button
												key={document.id}
												type="button"
												className={`flex items-center space-x-3 p-3 border rounded-md w-full text-left ${
													isAddingDocuments
														? "cursor-not-allowed bg-gray-100"
														: "hover:bg-gray-50 cursor-pointer"
												}`}
												onClick={handleToggle}
												onKeyDown={handleKeyDown}
												aria-pressed={isSelected}
												disabled={isAddingDocuments}
											>
												<Checkbox
													id={`doc-${document.id}`}
													checked={isSelected}
													onCheckedChange={(checked) =>
														handleDocumentToggle(document, !!checked)
													}
													disabled={isAddingDocuments}
												/>
												<div className="flex items-center space-x-3 flex-1">
													<FileText className="w-6 h-6 text-gray-500" />
													<div className="flex-1 min-w-0">
														<p className="text-sm font-medium truncate">
															{document.name}
														</p>
														<p className="text-xs text-gray-500">
															{document.type} •{" "}
															{formatSimpleDate(document.createdAt)}
														</p>
													</div>
												</div>
											</button>
										);
									})}
								</div>

								<div className="flex justify-between items-center mt-4">
									<p className="text-sm text-gray-600">
										{selectedDocuments.length} document(s) sélectionné(s)
									</p>
									<div className="space-x-2">
										<Button
											variant="outline"
											onClick={() => setIsExistingDocumentsDialogOpen(false)}
											disabled={isAddingDocuments}
										>
											Annuler
										</Button>
										<Button
											onClick={handleAttachSelectedDocuments}
											disabled={
												selectedDocuments.length === 0 || isAddingDocuments
											}
										>
											{isAddingDocuments ? (
												<>
													<Loader className="w-4 h-4 mr-2" />
													Ajout en cours...
												</>
											) : (
												`Attacher (${selectedDocuments.length})`
											)}
										</Button>
									</div>
								</div>
							</>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{attachments.length > 0 && (
				<div className="space-y-2">
					{attachments.map((attachment) => (
						<div
							key={attachment.id}
							className="flex items-center justify-between p-2 border rounded-md bg-gray-50"
						>
							<div className="flex items-center">
								{attachment.isUploading ? (
									<Loader className="mr-2" />
								) : (
									<Paperclip className="w-4 h-4 text-gray-500 mr-2" />
								)}
								<div>
									<p className="text-sm font-medium">
										{attachment.name}
										{attachment.isUploading && " (Uploading...)"}
									</p>
									<p className="text-xs text-gray-500">
										{formatFileSize(attachment.size)}
									</p>
								</div>
							</div>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => onAttachmentRemove(attachment.id)}
								aria-label="Remove attachment"
								disabled={attachment.isUploading}
							>
								<X className="w-4 h-4" />
							</Button>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
